import { IOgqlV1Column, IOgqlV1DataAndColumns } from '@opengov/ogql-types';
import {
  DateColumnName,
  EReportGridValueFormat,
  IDataValuesFormat,
  OG_META_DATA_KEY,
  SortDirection
} from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import { Set } from 'immutable';
import { isEmpty, uniqueId } from 'lodash';
import { DateTime } from 'luxon';
import { Decimal } from 'decimal.js';

import { nonGraphableKeys } from '../visualizations/constants';

export enum EQueryColumnSortType {
  DIMENSIONAL = 'dimensional',
  YEAR_DATE = 'yearDate',
  FULL_DATE = 'fullDate'
}
export interface IDimensionalAgGridRowItem {
  name: string[];
  queryColumns: string[];
  isGroup: boolean;
  isRowEmpty: boolean;
  rowId: string;
  [columnName: string]: any;
}

export interface IGroupByAgGridRowItem {
  isGroup: boolean;
  isRowEmpty: boolean;
  rowId: string;
  [columnName: string]: any;
}

export const graphToSummaryGridRowData = (
  chartData: IDataValuesFormat[],
  showName: string,
  isPercentOfTotal: boolean,
  emptyValuesText: string
): { [key: string]: string | number }[] => {
  if (chartData === undefined || chartData.length === 0) {
    return [];
  }

  const firstRow = chartData.reduce(
    (acc, data: IDataValuesFormat) => {
      const keys: string[] = Object.keys(data[OG_META_DATA_KEY].total);
      if (isPercentOfTotal) {
        // the percent of total query does not return a 'total' per cat like the other queries
        // And the value is from ogql.
        // Depending the number of decimal places, the sum of the percentages may not be 100
        // For example, 1/3 = 0.3333 , but 0.3333 * 3 = 0.9999
        // We don't do rounding as the first row is adding sum of the values,
        // and it was being calculated from ogql

        // If there is no percentage total, we need to calculate it
        if (!data[OG_META_DATA_KEY].percentTotal) {
          let vTotal = new Decimal(0);
          keys.forEach((key) => {
            if (data[key]) {
              vTotal = vTotal.add(new Decimal(data[OG_META_DATA_KEY].value[key] as number));
            }
          });
          acc[data.name] = vTotal.dividedBy(new Decimal(data[OG_META_DATA_KEY].aggregateTotal)).toNumber();
        } else {
          let pTotal = new Decimal(0);
          keys.forEach((key) => {
            if (data[key]) {
              pTotal = pTotal.add(new Decimal(data[OG_META_DATA_KEY].percentTotal[key] as number));
            }
          });
          // Product and Eng have standardized on 17 decimal places to preserve.
          const digitsAfterDecimalToShow = 17;
          acc[data.name] = parseFloat(pTotal.toFixed(digitsAfterDecimalToShow).replace(/\.?0+$/, ''));
        }
      } else {
        // all the keys have the same value which is the total for the "By" period which is what we want
        acc[`${data.name}`] = data[OG_META_DATA_KEY].total[keys[0]];
      }

      return acc;
    },
    { name: [isBlank(showName) ? emptyValuesText : showName] }
  );

  const rowKeys = extractColumnNames(chartData);

  return rowKeys.reduce(
    (acc, key) => {
      const keyDisplay = key === 'null' || !key ? emptyValuesText : key;
      const row = { name: [isBlank(showName) ? emptyValuesText : showName, keyDisplay] };
      chartData.forEach((data) => (row[`${data.name}`] = data[key]));
      acc.push(row);

      return acc;
    },
    [firstRow]
  );
};

export const extractColumnNames = (chartData: IDataValuesFormat[]): string[] => {
  // Set is a bit inappropriate here as the order is not guaranteed, though stable. Re-implementations of this
  // should be cautious and write tests that the order of keys are retained.
  return chartData
    .reduce<Set<string>>((acc, d) => Object.keys(d).reduce((keyAcc, key) => keyAcc.add(key), acc), Set())
    .filter((i) => !nonGraphableKeys.includes(i))
    .toArray();
};

// AG Grid generates this id for the tree-data group column, so it is what gets persisted
// as sortColumnName when a user sorts the row-label column. No row object carries a field
// under this key — the label lives in `name`, which is what autoColumnSorter reads.
export const AG_GRID_GROUP_COLUMN_ID = 'ag-Grid-AutoColumn';

const autoColumnSorter =
  (direction: SortDirection) =>
  (aRow, bRow): number => {
    const a = aRow.name[1];
    const b = bRow.name[1];

    // the blank checks are because the auto column doesn't have a name but we want it on top
    // this code is assuming only 1 parent/group/auto column in the array!
    // to make to work with multiple groups you would have to decern the sort order of the parents too
    if (isBlank(a)) {
      return -1;
    }

    if (isBlank(b)) {
      return 1;
    }

    if (direction === SortDirection.ASC) {
      return a.localeCompare(b);
    } else if (direction === SortDirection.DESC) {
      return b.localeCompare(a);
    }

    return 0;
  };

const numericalSorter =
  (direction: SortDirection, columnName: string) =>
  (a, b): number => {
    const aIsBlank = isBlank(a[columnName]);
    const bIsBlank = isBlank(b[columnName]);

    if (aIsBlank && bIsBlank) {
      return 0;
    }

    // ag-grid's defaultComparator reports a missing value as smaller and the grid then inverts the
    // whole comparator for a descending sort, so missing lands first ascending and last descending.
    // Returning a fixed "missing last" here instead put an embed in the opposite order from the
    // report it was built from, on every ascending sort over a column with gaps.
    if (aIsBlank) {
      return direction === SortDirection.DESC ? 1 : -1;
    }

    if (bIsBlank) {
      return direction === SortDirection.DESC ? -1 : 1;
    }

    if (direction === SortDirection.ASC) {
      return a[columnName] - b[columnName];
    }

    if (direction === SortDirection.DESC) {
      return b[columnName] - a[columnName];
    }

    return 0;
  };

export const rawToSorted = (data: any[], columnName: string, direction: SortDirection) => {
  const isAutoColumn = isBlank(columnName) || columnName === AG_GRID_GROUP_COLUMN_ID;
  const sorter = isAutoColumn ? autoColumnSorter : numericalSorter;
  return data.sort(sorter(direction, columnName));
};

const makeSortDateColumns =
  (dateFormat: string) =>
  (prevColumn: IOgqlV1Column, nextColumn: IOgqlV1Column): number => {
    const timeDifference = DateTime.fromFormat(prevColumn.label, dateFormat).diff(
      DateTime.fromFormat(nextColumn.label, dateFormat)
    );
    if (timeDifference.valueOf() > 0) {
      return 1;
    }
    if (timeDifference.valueOf() < 0) {
      return -1;
    }
    return 0;
  };

export const removeEmptyQueryResults = (queryResults: IOgqlV1DataAndColumns[]): IOgqlV1DataAndColumns[] => {
  return queryResults.reduce((filteredQueryResults, result) => {
    if (result.data.length && !isEmpty(result.data[0]) && result.columns.length) {
      filteredQueryResults.push(result);
    }
    return filteredQueryResults;
  }, []);
};

export const mergeColumnsFromMultipleQueries = (
  queryResults: IOgqlV1DataAndColumns[],
  queryColumnSortOrder?: EQueryColumnSortType
): IOgqlV1Column[] => {
  // We are using label here to remove duplicate columns, because column name is the same for generated columns
  // for different result sets.
  let sortFunction = (a, b) => 1;
  switch (queryColumnSortOrder) {
    case EQueryColumnSortType.YEAR_DATE:
      sortFunction = makeSortDateColumns('yyyy');
      break;
    case EQueryColumnSortType.FULL_DATE:
      sortFunction = makeSortDateColumns('yyyy-mm-dd');
      break;
    case EQueryColumnSortType.DIMENSIONAL:
    default:
      // We do not have the ability to sort by CoA Order
      // This functionality will be added once https://opengovinc.atlassian.net/browse/REP-4180 is complete
      sortFunction = (a, b) => 1;
  }
  return queryResults
    .reduce((mergedColumns, queryResult, index) => {
      if (index === 0) {
        return queryResult.columns.slice(1);
      } else {
        return [
          ...mergedColumns,
          ...queryResult.columns.slice(1).filter((column) => {
            return !mergedColumns.some((c) => c.label === column.label);
          })
        ];
      }
    }, [])
    .sort(sortFunction);
};

export const columnsSelectionToSortType = (dateColumnName: string): EQueryColumnSortType => {
  if (!dateColumnName) {
    return EQueryColumnSortType.DIMENSIONAL;
  }
  if (dateColumnName === DateColumnName.Year || dateColumnName === DateColumnName.FiscalYear) {
    return EQueryColumnSortType.YEAR_DATE;
  }
  return EQueryColumnSortType.FULL_DATE;
};

export const mapQueryRowDataToColumnLabel = (
  queryRowData: IOgqlV1DataAndColumns['data'],
  nonGroupColumns: IOgqlV1Column[],
  hasCodeColumn: boolean
): {
  [columnLabel: string]: any;
}[][] => {
  return queryRowData.map((row) => {
    const dataWithoutGroupColumn = row.slice(hasCodeColumn ? 2 : 1);
    return dataWithoutGroupColumn.reduce((acc, rowValue, index) => {
      acc[nonGroupColumns[index].label] = rowValue;
      return acc;
    }, {});
  });
};

export const ogqlUrnTypeToAgGrid = (type: string) => {
  if (type.includes(EReportGridValueFormat.NUMERIC)) {
    return EReportGridValueFormat.NUMERIC;
  }
  if (type.includes(EReportGridValueFormat.TEXT)) {
    return EReportGridValueFormat.TEXT;
  }
  return type;
};

export const ogqlColumnsToAgGrid = (columns: IOgqlV1Column[]) =>
  columns.map((c) => {
    const name = c.label;
    return {
      display_name: name,
      metadata: {
        facts: {},
        tags: {}
      },
      name,
      type: ogqlUrnTypeToAgGrid(c.logicalType),
      semantic_types: c.semanticTypes
    };
  });

export const ogqlDataToAgGrid = (
  data: { [columnName: string]: any }[],
  columns: any[],
  nameKeys: string[],
  isGroup: boolean
): IDimensionalAgGridRowItem[] => {
  return data.map((row, rowIndex) => {
    const name = [nameKeys[rowIndex]];
    // map the raw ogql row array to an object with key (column name) => value
    return columns.reduce(
      (acc, column) => {
        const datum = row[column.name];
        acc[column.name] = datum;
        if (datum) {
          return { ...acc, isRowEmpty: false };
        }
        return acc;
      },
      // queryColumns holds the ordering by name of the columns.
      // we need this to find the code columns in the innerRenderer
      { name, queryColumns: columns.map((c) => c.name), isGroup, isRowEmpty: true, rowId: uniqueId('rowId-') }
    );
  });
};

export const ogqlDataToAgGridGroupBy = (
  data: { [columnName: string]: any }[],
  columns: { name: string }[],
  isGroup: boolean,
  groupByColumnName: string
): IGroupByAgGridRowItem[] => {
  return data.map((row) => {
    // map the raw ogql row array to an object with key (column name) => value
    return columns.reduce(
      (acc, column) => {
        const datum = groupByColumnName === column.name ? '' : row[column.name];
        acc[column.name] = datum;
        if (datum) {
          return { ...acc, isRowEmpty: false };
        }
        return acc;
      },
      { isGroup, isRowEmpty: true, rowId: uniqueId('rowId-') }
    );
  });
};
